export const LANDING_INTENT_TYPES = {
  LOADING: 'loading',
  AUTH_REQUIRED: 'auth_required',
  CREATOR_RESERVED_OFFERING_UNFULFILLED:
    'creator_reserved_offering_unfulfilled',
  CREATOR_TASK_IN_PROGRESS: 'creator_task_in_progress',
  OWNER_TASK_COMPLETION_NEEDED: 'owner_task_completion_needed',
  OWNER_POSTED_TASK_FOR_USER: 'owner_posted_task_for_user',
  ACCEPTED_TASK_FOR_USER: 'accepted_task_for_user',
  RESERVED_OFFERING_FOR_USER: 'reserved_offering_for_user',
  BEST_LANDING_TASK: 'best_landing_task',
  USE_LOCAL: 'use_local',
};

export {
  COLLISION_SPECIALIST_PICKUP_ADDRESS,
  COLLISION_SPECIALIST_RIDE_INTENT,
  COLLISION_SPECIALIST_RIDE_SOURCE,
  COLLISION_SPECIALIST_RIDE_TEMPLATE,
  COLLISION_SPECIALIST_RIDE_TITLE,
  buildCollisionSpecialistRidePreset,
  getCollisionSpecialistRideSuggestedPrice,
  isCollisionSpecialistRideRequest,
} from '../../utils/localOpsCollisionSpecialistRide';

export const LOCALOPS_ACTIVE_ORDER_STATUSES = new Set(['reserved']);

export const cleanLaunchAttributionValue = (value, maxLength = 256) => {
  if (value === undefined || value === null) return '';
  return String(value).replace(/[\r\n\t]+/g, ' ').trim().slice(0, maxLength);
};

export const buildClientLaunchAttribution = ({
  queryParams,
  resumeLocalOps,
} = {}) => {
  const readParam = (key, maxLength) =>
    cleanLaunchAttributionValue(queryParams?.get?.(key), maxLength);
  const readResume = (key, maxLength) =>
    cleanLaunchAttributionValue(resumeLocalOps?.[key], maxLength);
  const attribution = {
    source: readParam('source', 120) || readResume('source', 120),
    src: readParam('src', 120) || readResume('src', 120),
    intent: readParam('intent', 80) || readResume('intent', 80),
    regionSlug:
      readParam('regionSlug', 120) || readResume('regionSlug', 120),
    marketSlug:
      readParam('marketSlug', 120) || readResume('marketSlug', 120),
    partnerSource:
      readParam('partnerSource', 120) || readResume('partnerSource', 120),
    partnerAction:
      readParam('partnerAction', 120) || readResume('partnerAction', 120),
    shareId: readParam('share', 256) || readResume('share', 256),
  };
  const hasAnyValue = Object.values(attribution).some(Boolean);
  if (!hasAnyValue) return null;
  return {
    ...attribution,
    capturedAt: new Date().toISOString(),
  };
};

export const getTaskIntentId = (task) => {
  const id = task?.taskId || task?.id || task?._id || null;
  return id == null ? null : String(id);
};

const OWNER_TASK_COMPLETION_STATUSES = new Set([
  'accepted',
  'started',
  'in_progress',
]);

const OWNER_TASK_POSTED_STATUSES = new Set([
  'created',
  'discoverable',
  'open',
]);

const normalizeTaskStatus = (task) =>
  String(task?.status || '').trim().toLowerCase();

const hasIdentityValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== '';

const matchesIdentityValue = (candidates, currentValue) =>
  hasIdentityValue(currentValue) &&
  (Array.isArray(candidates) ? candidates : [])
    .filter(hasIdentityValue)
    .some((value) => String(value) === String(currentValue));

const matchesTaskOwnerIdentity = ({
  task,
  currentPlayerId,
  currentPlayerHandle,
}) => {
  const creatorPlayerIdRaw = task?.creatorPlayerId;
  const creatorObject =
    creatorPlayerIdRaw && typeof creatorPlayerIdRaw === 'object'
      ? creatorPlayerIdRaw
      : null;
  const creatorRawString =
    typeof creatorPlayerIdRaw === 'string' ? creatorPlayerIdRaw : null;

  const idCandidates = [
    creatorObject?._id,
    creatorObject?.id,
    creatorRawString,
    task?.creator?._id,
    task?.creator?.id,
    task?.ownerId,
    task?.createdBy,
  ];
  const handleCandidates = [
    creatorObject?.playerId,
    creatorRawString,
    task?.creator?.playerId,
    task?.ownerHandle,
    task?.playerId,
  ];

  return (
    matchesIdentityValue(idCandidates, currentPlayerId) ||
    matchesIdentityValue(handleCandidates, currentPlayerHandle)
  );
};

export const taskHasAcceptedHelper = (task) =>
  [
    task?.acceptedPlayerId?._id,
    task?.acceptedPlayerId,
    task?.accepted?._id,
    task?.accepted?.id,
    task?.accepted?.playerId,
    task?.acceptedPlayer?._id,
    task?.acceptedPlayer?.id,
    task?.acceptedPlayer?.playerId,
    task?.acceptedPlayerHandle,
    task?.providerId?._id,
    task?.providerId,
    task?.provider?._id,
    task?.provider?.id,
    task?.provider?.playerId,
    task?.providerHandle,
  ].some(hasIdentityValue);

export const getOwnerTaskLandingIntentType = (task) => {
  const status = normalizeTaskStatus(task);
  if (OWNER_TASK_COMPLETION_STATUSES.has(status) && taskHasAcceptedHelper(task)) {
    return LANDING_INTENT_TYPES.OWNER_TASK_COMPLETION_NEEDED;
  }
  if (OWNER_TASK_POSTED_STATUSES.has(status)) {
    return LANDING_INTENT_TYPES.OWNER_POSTED_TASK_FOR_USER;
  }
  return null;
};

export const getOwnerTaskLandingLabel = (task) =>
  getOwnerTaskLandingIntentType(task) ===
  LANDING_INTENT_TYPES.OWNER_TASK_COMPLETION_NEEDED
    ? 'Review and verify completion'
    : 'View your live task';

export const hasUsableLandingAmountLabel = (value) =>
  /^\$\d/.test(String(value || '').trim());

export const getLandingEarnTaskLabel = (landingAmountLabel) =>
  hasUsableLandingAmountLabel(landingAmountLabel)
    ? `Earn ${landingAmountLabel} from this task`
    : 'Review local task';

export const getLandingEarnHeadline = (landingAmountLabel) =>
  hasUsableLandingAmountLabel(landingAmountLabel)
    ? `Earn ${landingAmountLabel} nearby`
    : 'Review local task';

const getOwnerTaskSortTimestamp = (task) => {
  const parsed =
    Date.parse(task?.updatedAt || task?.acceptedAt || task?.createdAt || '') || 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

export const selectOwnerLandingTask = ({
  tasks = [],
  currentPlayerId,
  currentPlayerHandle,
} = {}) => {
  const ranked = (Array.isArray(tasks) ? tasks : [])
    .map((task, index) => {
      if (!task) return null;
      if (
        !matchesTaskOwnerIdentity({
          task,
          currentPlayerId,
          currentPlayerHandle,
        })
      ) {
        return null;
      }
      const intentType = getOwnerTaskLandingIntentType(task);
      if (!intentType) return null;
      return {
        task,
        index,
        intentType,
        urgency:
          intentType === LANDING_INTENT_TYPES.OWNER_TASK_COMPLETION_NEEDED
            ? 2
            : 1,
        timestamp: getOwnerTaskSortTimestamp(task),
        id: getTaskIntentId(task) || '',
      };
    })
    .filter(Boolean);

  ranked.sort((a, b) => {
    if (b.urgency !== a.urgency) return b.urgency - a.urgency;
    if (b.timestamp !== a.timestamp) return b.timestamp - a.timestamp;
    if (a.index !== b.index) return a.index - b.index;
    return a.id.localeCompare(b.id);
  });

  return ranked[0]?.task || null;
};

export const getOfferingIntentId = (offering) => {
  const id = offering?.offeringId || offering?.id || offering?._id || null;
  return id == null ? null : String(id);
};

export const getOrderIntentStatus = (order) =>
  String(order?.status || '').trim().toLowerCase();

export const getOrderIntentOfferingId = (order) => {
  const id =
    order?.offeringId ||
    order?.offering?._id ||
    order?.offering?.id ||
    (typeof order?.offering === 'string' ? order.offering : null) ||
    null;
  return id == null ? null : String(id);
};

export const buildOrderBackedOfferingTarget = (
  order,
  snapshotOffering = null,
  normalizeOffering = (value) => value,
) => {
  if (snapshotOffering) return snapshotOffering;
  const fallbackId = getOrderIntentOfferingId(order);
  if (!fallbackId) return null;
  if (order?.offering && typeof order.offering === 'object') {
    const normalized = normalizeOffering(order.offering);
    if (normalized) {
      const normalizedId = getOfferingIntentId(normalized) || fallbackId;
      return {
        ...normalized,
        offeringId:
          normalized.offeringId || normalized.id || normalized._id || normalizedId,
        id: normalized.id || normalized._id || normalizedId,
        _id: normalized._id || normalized.id || normalizedId,
        __landingOrderBacked: true,
      };
    }
  }
  return {
    offeringId: fallbackId,
    id: fallbackId,
    _id: fallbackId,
    status: 'reserved',
    __landingOrderBacked: true,
  };
};

export const resolveLandingIntent = (state, featureFlags = {}) => {
  const skipTypes = state?.skipTypes instanceof Set ? state.skipTypes : new Set();
  const shouldSkip = (type) => skipTypes.has(type);
  const enableCreatorIntent = featureFlags?.enableCreatorIntent === true;
  const creatorReservedOfferings = Array.isArray(state?.creatorReservedOfferings)
    ? state.creatorReservedOfferings
    : [];
  const creatorActiveTasks = Array.isArray(state?.creatorActiveTasks)
    ? state.creatorActiveTasks
    : [];
  const ownerTaskForUser = state?.ownerTaskForUser || null;
  const ownerTaskIntentType = getOwnerTaskLandingIntentType(ownerTaskForUser);
  const intents = [];
  const pushIntent = (intent) => {
    if (!intent || shouldSkip(intent.type)) return;
    intents.push(intent);
  };

  if (state?.acceptedTaskForUser) {
    pushIntent({
      type: LANDING_INTENT_TYPES.ACCEPTED_TASK_FOR_USER,
      label: 'Resume accepted task (You accepted)',
      target: {
        kind: 'task',
        id: getTaskIntentId(state.acceptedTaskForUser) || undefined,
      },
      priority: 600,
    });
  }
  if (state?.reservedOfferingForUser) {
    pushIntent({
      type: LANDING_INTENT_TYPES.RESERVED_OFFERING_FOR_USER,
      label: 'Resume reservation',
      target: {
        kind: 'offering',
        id: getOfferingIntentId(state.reservedOfferingForUser) || undefined,
      },
      priority: 550,
    });
  }
  if (state?.bestLandingTask) {
    pushIntent({
      type: LANDING_INTENT_TYPES.BEST_LANDING_TASK,
      label: getLandingEarnTaskLabel(state?.landingAmountLabel),
      target: {
        kind: 'task',
        id: getTaskIntentId(state.bestLandingTask) || undefined,
      },
      priority: 575,
    });
  }
  if (ownerTaskIntentType === LANDING_INTENT_TYPES.OWNER_TASK_COMPLETION_NEEDED) {
    pushIntent({
      type: LANDING_INTENT_TYPES.OWNER_TASK_COMPLETION_NEEDED,
      label: getOwnerTaskLandingLabel(ownerTaskForUser),
      target: {
        kind: 'task',
        id: getTaskIntentId(ownerTaskForUser) || undefined,
      },
      priority: 590,
    });
  }
  if (ownerTaskIntentType === LANDING_INTENT_TYPES.OWNER_POSTED_TASK_FOR_USER) {
    pushIntent({
      type: LANDING_INTENT_TYPES.OWNER_POSTED_TASK_FOR_USER,
      label: getOwnerTaskLandingLabel(ownerTaskForUser),
      target: {
        kind: 'task',
        id: getTaskIntentId(ownerTaskForUser) || undefined,
      },
      priority: 525,
    });
  }
  if (enableCreatorIntent && creatorReservedOfferings.length > 0) {
    const target = creatorReservedOfferings[0];
    pushIntent({
      type: LANDING_INTENT_TYPES.CREATOR_RESERVED_OFFERING_UNFULFILLED,
      label:
        creatorReservedOfferings.length > 1
          ? `Resume reservations (${creatorReservedOfferings.length})`
          : 'Resume reservation',
      target: {
        kind: 'offering',
        id: getOfferingIntentId(target) || undefined,
      },
      priority: 500,
    });
  }
  if (enableCreatorIntent && creatorActiveTasks.length > 0) {
    const target = creatorActiveTasks[0];
    pushIntent({
      type: LANDING_INTENT_TYPES.CREATOR_TASK_IN_PROGRESS,
      label: 'Resume active task',
      target: { kind: 'task', id: getTaskIntentId(target) || undefined },
      priority: 450,
    });
  }
  pushIntent({
    type: LANDING_INTENT_TYPES.USE_LOCAL,
    label: 'Use JGK locally',
    target: { kind: 'use_locally' },
    priority: 100,
  });

  const baseIntent = [...intents].sort(
    (a, b) => (Number(b?.priority) || 0) - (Number(a?.priority) || 0),
  )[0] || {
    type: LANDING_INTENT_TYPES.USE_LOCAL,
    label: 'Use JGK locally',
    target: { kind: 'use_locally' },
    priority: 100,
  };

  if (!state?.landingReady) {
    return {
      type: LANDING_INTENT_TYPES.LOADING,
      label: 'Loading...',
      target: { kind: 'use_locally' },
      priority: 900,
    };
  }
  if (state?.landingAuthRequired) {
    return {
      ...baseIntent,
      type: LANDING_INTENT_TYPES.AUTH_REQUIRED,
      label: 'Sign in to continue',
      priority: 800,
    };
  }
  return baseIntent;
};

export const resolveLandingPrimaryAction = ({
  runtimeState,
  featureFlags = {},
  tasks = [],
  offerings = [],
  player = null,
}) => {
  const resumeIntentTypes = new Set([
    LANDING_INTENT_TYPES.CREATOR_RESERVED_OFFERING_UNFULFILLED,
    LANDING_INTENT_TYPES.CREATOR_TASK_IN_PROGRESS,
    LANDING_INTENT_TYPES.OWNER_TASK_COMPLETION_NEEDED,
    LANDING_INTENT_TYPES.OWNER_POSTED_TASK_FOR_USER,
    LANDING_INTENT_TYPES.ACCEPTED_TASK_FOR_USER,
    LANDING_INTENT_TYPES.RESERVED_OFFERING_FOR_USER,
  ]);
  const skippedTypes = new Set();
  const taskList = Array.isArray(tasks) ? tasks : [];
  const offeringList = Array.isArray(offerings) ? offerings : [];

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const intent = resolveLandingIntent(
      { ...(runtimeState || {}), skipTypes: skippedTypes },
      featureFlags,
    );
    if (!intent) break;

    if (!player && intent.target?.kind !== 'use_locally') {
      return { type: 'require_auth', intentType: intent.type };
    }

    if (intent.target?.kind === 'task') {
      const resolvedId = intent.target?.id ? String(intent.target.id) : null;
      const match = resolvedId
        ? taskList.find((item) => getTaskIntentId(item) === resolvedId)
        : null;
      if (!match) {
        skippedTypes.add(intent.type);
        continue;
      }
      return {
        type: 'select_task',
        intentType: intent.type,
        task: match,
        dismissLanding: resumeIntentTypes.has(intent.type),
      };
    }

    if (intent.target?.kind === 'offering') {
      const resolvedId = intent.target?.id ? String(intent.target.id) : null;
      const snapshotMatch = resolvedId
        ? offeringList.find((item) => getOfferingIntentId(item) === resolvedId)
        : null;
      const obligationCandidates = [
        runtimeState?.reservedOfferingForUser,
        ...(Array.isArray(runtimeState?.creatorReservedOfferings)
          ? runtimeState.creatorReservedOfferings
          : []),
      ].filter(Boolean);
      const obligationMatch =
        !snapshotMatch && resolvedId
          ? obligationCandidates.find(
              (item) => getOfferingIntentId(item) === resolvedId,
            ) || null
          : null;
      const match = snapshotMatch || obligationMatch;
      if (!match) {
        skippedTypes.add(intent.type);
        continue;
      }
      return {
        type: 'select_offering',
        intentType: intent.type,
        offering: match,
        dismissLanding: resumeIntentTypes.has(intent.type),
      };
    }

    if (intent.target?.kind === 'use_locally') {
      return { type: 'use_locally', intentType: intent.type };
    }

    skippedTypes.add(intent.type);
  }

  return { type: 'use_locally', intentType: LANDING_INTENT_TYPES.USE_LOCAL };
};

export const shouldShowLandingSheet = ({
  landingDismissed,
  hasResumeIntent,
  postCompletionOpen,
  selectedTask,
  selectedOffering,
  isCreateOpen,
  isTopUpOpen,
  localOpsFeedOpen,
}) =>
  !landingDismissed &&
  !hasResumeIntent &&
  !postCompletionOpen &&
  !selectedTask &&
  !selectedOffering &&
  !isCreateOpen &&
  !isTopUpOpen &&
  !localOpsFeedOpen;

export const buildCreateTaskPresetFromOffering = ({
  offering,
  mode,
  defaultCenter,
  mapCenter,
  localOpsCenter,
  normalizeCoordinates,
}) => {
  const coords =
    typeof normalizeCoordinates === 'function'
      ? normalizeCoordinates(offering)
      : null;
  const origin = coords
    ? { lat: coords[1], lon: coords[0] }
    : defaultCenter || mapCenter || localOpsCenter || null;
  const destination =
    mode === 'delivery' && localOpsCenter ? { ...localOpsCenter } : null;
  return {
    type: mode === 'delivery' ? 'task_delivery' : 'task_errand',
    title: offering?.title || offering?.name,
    description: offering?.description,
    category: offering?.category,
    origin,
    destination,
  };
};

export const shouldAutoInjectCheckoutSuccess = ({
  hasCheckoutParam = false,
  pendingIntent,
  startedAt,
  now = Date.now(),
  recentWindowMs = 30 * 60 * 1000,
}) =>
  !hasCheckoutParam &&
  !!pendingIntent &&
  Number.isFinite(startedAt) &&
  now - startedAt < recentWindowMs;

export const getTrustGateRetryAction = ({
  payload,
  hasPlayerToken,
  now = Date.now(),
  maxAgeMs = 10 * 60 * 1000,
}) => {
  if (!payload?.taskId || !hasPlayerToken) return { type: 'skip' };
  if (payload.ts && now - Number(payload.ts) > maxAgeMs) {
    return { type: 'clear_expired' };
  }
  return { type: 'retry', taskId: String(payload.taskId) };
};

export const getPersistedCompletionAction = ({
  taskId,
  seen,
  timestamp,
  now = Date.now(),
  staleMs = 24 * 60 * 60 * 1000,
}) => {
  if (!taskId || seen) return { type: 'skip' };
  if (timestamp && now - timestamp > staleMs) {
    return { type: 'clear_stale' };
  }
  return { type: 'persisted_unseen', taskId: String(taskId) };
};
